import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { TwoFactorDialog, ForcePasswordChangeDialog } from "@/components/LoginFlowDialogs";
import { checkSession } from "@/utils/auth";
import { useLoginFlow } from "@/hooks/useLoginFlow";

const HRLogin = () => {
  const [username, setUsername] = useState("hr");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { handleLogin, loading, flow, setFlow, handleOtpVerified, handlePasswordChanged } = useLoginFlow("hr", "/hr/dashboard");

  useEffect(() => {
    checkSession().then(session => {
      if (session.authenticated && (session.roles.includes("hr") || session.is_master)) {
        navigate("/hr/dashboard");
      }
    });
  }, [navigate]);

  const onSubmit = (e) => { e.preventDefault(); handleLogin(username, password); };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "radial-gradient(ellipse at center, #0f1729 0%, #000000 70%)" }}>
      <div className="max-w-md w-full">
        <div className="mb-6">
          <Link to="/"><Button variant="ghost" className="text-white hover:bg-white/20"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Home</Button></Link>
        </div>
        <Card className="bg-gray-900/80 border-teal-500/30 backdrop-blur">
          <CardContent className="pt-8 pb-8 px-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto rounded-xl bg-teal-500/20 border border-teal-500/40 flex items-center justify-center mb-4">
                <UserCheck className="h-8 w-8 text-teal-400" />
              </div>
              <h1 className="text-2xl font-bold text-teal-400">HR Access</h1>
              <p className="text-gray-400 text-sm mt-1">Attendance & Payroll</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <Label className="text-gray-300">Username</Label>
                <Input value={username} onChange={e => setUsername(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="hr-username" />
              </div>
              <div className="relative">
                <Label className="text-gray-300">Password</Label>
                <Input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white mt-1 pr-10" data-testid="hr-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-400">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold" data-testid="hr-login-btn">
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <TwoFactorDialog
        open={!!flow.twoFactor?.open}
        onOpenChange={(o) => setFlow((f) => ({ ...f, twoFactor: o ? f.twoFactor : null }))}
        role="hr" emailHint={flow.twoFactor?.emailHint} devOtp={flow.twoFactor?.devOtp}
        onVerified={handleOtpVerified}
      />
      <ForcePasswordChangeDialog
        open={!!flow.forcePwd?.open}
        onOpenChange={(o) => setFlow((f) => ({ ...f, forcePwd: o ? f.forcePwd : null }))}
        role="hr" currentPassword={flow.forcePwd?.currentPassword || ""}
        onChanged={handlePasswordChanged}
      />
    </div>
  );
};

export default HRLogin;
